import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  CreditCard,
  Eye,
  IndianRupee,
  ReceiptText,
  Store,
  Truck,
  Users,
  X,
} from "lucide-react";
import { orderAnalyticsService } from "../../../services/orderAnalytics.service";
import { warehouseService } from "../../../services/warehouse.service";
import { ReportsService } from "../../../services/reports.service";
import DriverDetailModal from "./components/DriverDetailModal";

const PINK = "#df6374";

const STATUS_OPTIONS = [
  { value: "", label: "All status" },
  { value: "PLACED", label: "Placed" },
  { value: "PROCESSING", label: "Processing" },
  { value: "PACKED", label: "Packed" },
  { value: "READY_FOR_DISPATCH", label: "Ready for dispatch" },
  { value: "DISPATCHED", label: "Dispatched" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "CANCELLED", label: "Cancelled" },
];

const PAYMENT_OPTIONS = [
  { value: "", label: "All payments" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
  { value: "REFUNDED", label: "Refunded" },
];

const PERIOD_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(num(value));
}

function titleCase(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickData(res) {
  return res?.data ?? res;
}

function pickList(res) {
  const data = pickData(res);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data?.products)) return data.products;
  if (Array.isArray(data?.warehouses)) return data.warehouses;
  if (Array.isArray(data?.trend)) return data.trend;
  if (Array.isArray(data?.salesTrend)) return data.salesTrend;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.rows)) return data.data.rows;
  if (Array.isArray(data?.data?.orders)) return data.data.orders;
  if (Array.isArray(data?.data?.products)) return data.data.products;
  if (Array.isArray(data?.data?.warehouses)) return data.data.warehouses;
  if (Array.isArray(data?.data?.trend)) return data.data.trend;
  if (Array.isArray(data?.data?.salesTrend)) return data.data.salesTrend;
  return [];
}

function pickObject(res) {
  const data = pickData(res);
  if (data?.summary && typeof data.summary === "object") return data.summary;
  if (data?.data?.summary && typeof data.data.summary === "object") return data.data.summary;
  if (data?.data && !Array.isArray(data.data) && typeof data.data === "object") return data.data;
  if (data && !Array.isArray(data) && typeof data === "object") return data;
  return {};
}

function pickMeta(res) {
  const data = pickData(res);
  return (
    data?.meta ||
    data?.pagination ||
    data?.data?.meta ||
    data?.data?.pagination ||
    res?.meta ||
    res?.pagination ||
    {}
  );
}

function normalizeWarehouse(item) {
  return {
    id: item?.id || item?._id || item?.warehouseId || item?.warehouse_id || "",
    name:
      item?.name ||
      item?.warehouseName ||
      item?.warehouse_name ||
      item?.title ||
      item?.code ||
      "Warehouse",
  };
}

function normalizeTrend(item, index, period) {
  const rawBucket =
    item?.bucket ||
    item?.date ||
    item?.day ||
    item?.week ||
    item?.month ||
    item?.label ||
    item?.createdAt ||
    item?.created_at ||
    index;

  return {
    key: String(rawBucket),
    label: formatBucket(rawBucket, period, index),
    value: num(
      item?.totalSales ??
        item?.total_sales ??
        item?.sales ??
        item?.revenue ??
        item?.totalRevenue ??
        item?.total_revenue ??
        item?.grossSales ??
        item?.gross_sales ??
        item?.netSales ??
        item?.net_sales ??
        item?.grandTotal ??
        item?.grand_total ??
        item?.total ??
        item?.amount ??
        item?.value ??
        item?.orderCount ??
        item?.totalOrders
    ),
    orders: num(item?.totalOrders ?? item?.total_orders ?? item?.orders ?? item?.orderCount ?? item?.count),
  };
}

function normalizeProduct(item, index) {
  return {
    id: item?.productId || item?.product_id || item?.id || item?._id || index,
    name: item?.productName || item?.product_name || item?.name || item?.title || "-",
    orders: num(item?.orderCount ?? item?.order_count ?? item?.orders ?? item?.totalOrders),
    revenue: num(item?.totalSales ?? item?.total_sales ?? item?.totalRevenue ?? item?.total_revenue ?? item?.revenue ?? item?.sales),
  };
}

function normalizeOrder(item, index) {
  const pricing = item?.pricing || {};
  return {
    id: item?.id || item?._id || item?.orderId || item?.order_id || index,
    orderNo:
      item?.orderNumber ||
      item?.order_no ||
      item?.orderCode ||
      item?.code ||
      item?.id ||
      item?._id ||
      "-",
    warehouse:
      item?.warehouseName ||
      item?.warehouse_name ||
      item?.warehouse?.name ||
      item?.warehouseTitle ||
      "-",
    status: item?.status || item?.orderStatus || item?.order_status || "-",
    items: num(item?.itemCount ?? item?.itemsCount ?? item?.items_count ?? item?.items?.length),
    qty: num(item?.totalQty ?? item?.total_qty ?? item?.quantity ?? item?.qty),
    total: num(
      item?.grandTotal ??
        item?.grand_total ??
        item?.totalAmount ??
        item?.total_amount ??
        item?.totalRevenue ??
        item?.total_revenue ??
        pricing?.grandTotal ??
        pricing?.grand_total
    ),
    createdAt: item?.createdAt || item?.created_at || item?.orderedAt || item?.ordered_at || "",
  };
}

function buildTrendFromOrders(orderItems = [], period = "day") {
  const grouped = new Map();

  orderItems.forEach((order) => {
    const d = order?.createdAt ? new Date(order.createdAt) : null;
    if (!d || Number.isNaN(d.getTime())) return;

    let key = d.toISOString().slice(0, 10);
    if (period === "month") key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    if (period === "week") {
      const weekStart = new Date(d);
      const day = weekStart.getDay() || 7;
      weekStart.setDate(weekStart.getDate() - day + 1);
      key = weekStart.toISOString().slice(0, 10);
    }

    const prev = grouped.get(key) || { key, label: formatBucket(key, period), value: 0, orders: 0 };
    prev.value += num(order?.total);
    prev.orders += 1;
    grouped.set(key, prev);
  });

  return Array.from(grouped.values()).sort((a, b) => String(a.key).localeCompare(String(b.key)));
}

function formatBucket(value, period, index = 0) {
  if (value === index || value === undefined || value === null || value === "") return `${index + 1}`;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    if (period === "month") {
      return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
    }
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}


function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function getDetailPayload(res) {
  return res?.data || res || null;
}

function getDetailOrder(detail = {}) {
  return detail?.order || detail || {};
}

function getDetailItems(detail = {}) {
  const order = getDetailOrder(detail);
  return Array.isArray(detail?.items) ? detail.items : Array.isArray(order?.items) ? order.items : [];
}

function getDetailDelivery(detail = {}) {
  const order = getDetailOrder(detail);
  return detail?.delivery || detail?.deliveryTask || order?.delivery || order?.deliveryTask || null;
}

function getDetailPricing(order = {}) {
  const pricing = order?.pricing || {};
  return {
    subtotal: firstValue(order?.subtotal, order?.sub_total, pricing?.subtotal, pricing?.sub_total, 0),
    deliveryFee: firstValue(order?.deliveryFee, order?.delivery_fee, pricing?.deliveryFee, pricing?.delivery_fee, 0),
    handlingCharge: firstValue(order?.handlingCharge, order?.handling_charge, pricing?.handlingCharge, pricing?.handling_charge, 0),
    rainCharge: firstValue(order?.rainCharge, order?.rain_charge, pricing?.rainCharge, pricing?.rain_charge, 0),
    taxAmount: firstValue(order?.taxAmount, order?.tax_amount, pricing?.taxAmount, pricing?.tax_amount, 0),
    discountAmount: firstValue(order?.discountAmount, order?.discount_amount, pricing?.discountAmount, pricing?.discount_amount, 0),
    grandTotal: firstValue(order?.grandTotal, order?.grand_total, order?.totalAmount, order?.total_amount, pricing?.grandTotal, pricing?.grand_total, 0),
  };
}

function getDetailAddress(order = {}) {
  return order?.address || order?.deliveryAddress || order?.delivery_address || order?.address_snapshot || {};
}

function getProductName(item = {}) {
  const p = item?.product || item?.productSnapshot || item?.product_snapshot || {};
  return firstValue(p?.displayName, p?.display_name, p?.productName, p?.product_name, p?.name, item?.name, item?.productName, item?.product_name, "-");
}

function getProductSku(item = {}) {
  const p = item?.product || item?.productSnapshot || item?.product_snapshot || {};
  return firstValue(item?.variantSku, item?.variant_sku, p?.variantSku, p?.variant_sku, p?.sku, item?.sku, "-");
}

function getVariantLabel(item = {}) {
  const p = item?.product || item?.productSnapshot || item?.product_snapshot || {};
  return [
    firstValue(item?.variantName, item?.variant_name, p?.variantName, p?.variant_name),
    firstValue(item?.packLabel, item?.pack_label, p?.packLabel, p?.pack_label),
  ].filter(Boolean).join(" / ") || "-";
}

function DetailValue({ label, value, className = "" }) {
  return (
    <div className={`rounded-[14px] border border-[#f3c7ce] bg-white px-4 py-3 ${className}`}>
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#df6374]">{label}</p>
      <p className="mt-1 break-words text-[13px] font-bold text-slate-700">{value ?? "-"}</p>
    </div>
  );
}

function DetailPill({ children, tone = "slate" }) {
  const cls = tone === "green"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : tone === "red"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : tone === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-[12px] font-black ${cls}`}>{children}</span>;
}

function statusTone(status) {
  const s = String(status || "").toUpperCase();
  if (["PAID", "PLACED", "DELIVERED", "COMPLETED"].includes(s)) return "green";
  if (["FAILED", "PAYMENT_FAILED", "CANCELLED", "REFUNDED"].includes(s)) return "red";
  if (["PENDING", "PAYMENT_PENDING", "PAYMENT_PENDING_ON_DELIVERY"].includes(s)) return "amber";
  return "slate";
}


function normalizeWebhookEvent(item = {}) {
  return {
    id: item?.id || null,
    eventId: firstValue(item?.eventId, item?.event_id, item?.razorpayEventId, item?.razorpay_event_id),
    eventName: firstValue(item?.eventName, item?.event_name, item?.event),
    signatureValid: Boolean(firstValue(item?.signatureValid, item?.signature_valid, false)),
    processingStatus: firstValue(item?.processingStatus, item?.processing_status, "RECEIVED"),
    handled: Boolean(firstValue(item?.handled, false)),
    handlerAction: firstValue(item?.handlerAction, item?.handler_action),
    skipReason: firstValue(item?.skipReason, item?.skip_reason),
    errorMessage: firstValue(item?.errorMessage, item?.error_message),
    entityStatus: firstValue(item?.entityStatus, item?.entity_status, item?.status),
    amount: firstValue(item?.amount, null),
    currency: firstValue(item?.currency, "INR"),
    razorpayOrderId: firstValue(item?.razorpayOrderId, item?.razorpay_order_id),
    razorpayPaymentId: firstValue(item?.razorpayPaymentId, item?.razorpay_payment_id),
    razorpayRefundId: firstValue(item?.razorpayRefundId, item?.razorpay_refund_id),
    razorpayQrCodeId: firstValue(item?.razorpayQrCodeId, item?.razorpay_qr_code_id),
    payload: item?.payload && typeof item.payload === "object" ? item.payload : {},
    receivedAt: firstValue(item?.receivedAt, item?.received_at),
    processedAt: firstValue(item?.processedAt, item?.processed_at),
  };
}

function getReportWebhookEvents(detail = {}, order = {}) {
  const source = Array.isArray(detail?.webhookEvents)
    ? detail.webhookEvents
    : Array.isArray(detail?.webhook_events)
      ? detail.webhook_events
      : Array.isArray(order?.webhookEvents)
        ? order.webhookEvents
        : Array.isArray(order?.webhook_events)
          ? order.webhook_events
          : [];
  return source.map(normalizeWebhookEvent);
}

function reportWebhookStatusClass(status) {
  const value = String(status || "").toUpperCase();
  if (value === "PROCESSED") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (value === "FAILED") return "border-rose-200 bg-rose-50 text-rose-700";
  if (value === "SKIPPED") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function formatWebhookAmount(amount, currency = "INR") {
  if (amount === null || amount === undefined || amount === "") return "-";
  const subunits = Number(amount);
  if (!Number.isFinite(subunits)) return String(amount);
  const value = subunits / 100;
  if (String(currency || "INR").toUpperCase() === "INR") return `₹ ${money(value)}`;
  return `${String(currency || "").toUpperCase()} ${value.toFixed(2)}`;
}

function RazorpayWebhookReportCard({ detail, order }) {
  const events = getReportWebhookEvents(detail, order);
  const provider = String(order?.paymentProvider || order?.payment_provider || "").toUpperCase();
  const latest = events[0] || null;

  if (provider !== "RAZORPAY" && !events.length) return null;

  return (
    <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h4 className="flex items-center gap-2 text-[15px] font-black text-black">
            <CreditCard size={17} className="text-[#df6374]" />
            Razorpay Webhook Audit
          </h4>
          <p className="mt-1 text-[12px] font-semibold text-slate-400">
            Admin-only payment event history received and processed by order-service.
          </p>
        </div>
        <span className="w-fit rounded-full border border-[#f3c7ce] bg-[#fff7f8] px-3 py-1 text-[11px] font-black text-[#df6374]">
          {events.length} event{events.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-4 grid gap-3 rounded-[12px] bg-slate-50 p-3 text-[12px] text-slate-700 md:grid-cols-2 xl:grid-cols-4">
        <div><span className="font-black">Latest event:</span> {latest?.eventName || "-"}</div>
        <div><span className="font-black">Provider status:</span> {latest?.entityStatus || order?.gatewayPaymentStatus || order?.gateway_payment_status || "-"}</div>
        <div><span className="font-black">Order ID:</span> <span className="break-all">{order?.razorpayOrderId || order?.razorpay_order_id || latest?.razorpayOrderId || "-"}</span></div>
        <div><span className="font-black">Payment ID:</span> <span className="break-all">{order?.razorpayPaymentId || order?.razorpay_payment_id || latest?.razorpayPaymentId || "-"}</span></div>
      </div>

      {!events.length ? (
        <div className="mt-4 rounded-[12px] border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] font-bold text-amber-700">
          No webhook event is linked to this order. This can be valid when no Razorpay payment attempt was created and reconciliation closed the pending order.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {events.map((item, index) => (
            <div key={item?.eventId || item?.id || index} className="rounded-[14px] border border-slate-200 p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13px] font-black text-slate-800">{item?.eventName || "Unknown event"}</span>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${reportWebhookStatusClass(item?.processingStatus)}`}>
                      {item?.processingStatus || "RECEIVED"}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${item?.handled ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                      {item?.handled ? "HANDLED" : "NOT HANDLED"}
                    </span>
                  </div>
                  <div className="mt-1 break-all text-[11px] text-slate-400">Event ID: {item?.eventId || "-"}</div>
                </div>
                <div className="text-[11px] font-semibold text-slate-500">Received: {formatDateTime(item?.receivedAt)}</div>
              </div>

              <div className="mt-3 grid gap-2 text-[12px] text-slate-700 md:grid-cols-2 xl:grid-cols-4">
                <div><span className="font-black">Entity status:</span> {item?.entityStatus || "-"}</div>
                <div><span className="font-black">Handler:</span> {item?.handlerAction || "-"}</div>
                <div><span className="font-black">Signature:</span> {item?.signatureValid ? "Verified" : "Not verified"}</div>
                <div><span className="font-black">Amount:</span> {formatWebhookAmount(item?.amount, item?.currency)}</div>
                <div><span className="font-black">Payment ID:</span> <span className="break-all">{item?.razorpayPaymentId || "-"}</span></div>
                <div><span className="font-black">QR ID:</span> <span className="break-all">{item?.razorpayQrCodeId || "-"}</span></div>
                <div><span className="font-black">Refund ID:</span> <span className="break-all">{item?.razorpayRefundId || "-"}</span></div>
                <div><span className="font-black">Processed:</span> {formatDateTime(item?.processedAt)}</div>
                {item?.skipReason ? <div className="md:col-span-2"><span className="font-black">Skip reason:</span> {item.skipReason}</div> : null}
                {item?.errorMessage ? <div className="text-rose-700 md:col-span-2"><span className="font-black">Error:</span> {item.errorMessage}</div> : null}
              </div>

              <details className="mt-3 rounded-[10px] border border-slate-200 bg-slate-50">
                <summary className="cursor-pointer px-3 py-2 text-[12px] font-black text-slate-700">
                  View exact webhook payload
                </summary>
                <pre className="max-h-[360px] overflow-auto border-t border-slate-200 p-3 text-[11px] leading-5 text-slate-700">
                  {JSON.stringify(item?.payload || {}, null, 2)}
                </pre>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderReportDetailModal({ open, loading, error, detail, onClose, onOpenDriver }) {
  if (!open) return null;

  const order = getDetailOrder(detail || {});
  const items = getDetailItems(detail || {});
  const delivery = getDetailDelivery(detail || {});
  const pricing = getDetailPricing(order);
  const address = getDetailAddress(order);
  const warehouse = order?.warehouse || {};
  const orderNo = firstValue(order?.orderNumber, order?.order_number, order?.orderNo, order?.id, "-");
  const addressText = [address?.addressLine1, address?.address_line1, address?.line1, address?.addressLine2, address?.address_line2, address?.line2]
    .filter(Boolean)
    .join(", ") || "-";
  const partner = delivery?.partner || delivery?.deliveryPartner || delivery?.delivery_partner || {};
  const driverName = firstValue(
    delivery?.partnerName,
    delivery?.partner_name,
    delivery?.driverName,
    delivery?.driver_name,
    partner?.name,
    partner?.fullName,
    partner?.full_name
  );
  const driverCode = firstValue(delivery?.partnerCode, delivery?.partner_code, partner?.partnerCode, partner?.partner_code, partner?.partnerId, partner?.partner_id);
  const driverPhone = firstValue(delivery?.partnerPhone, delivery?.partner_phone, delivery?.driverPhone, delivery?.driver_phone, partner?.phone);
  const vehicleText = [
    firstValue(delivery?.vehicleType, delivery?.vehicle_type, partner?.vehicleType, partner?.vehicle_type),
    firstValue(delivery?.vehicleNumber, delivery?.vehicle_number, partner?.vehicleNumber, partner?.vehicle_number),
  ].filter(Boolean).join(" / ") || "-";
  const driverDisplay = driverName ? `${driverName}${driverCode ? ` (${driverCode})` : ""}` : "-";
  const driverRef = firstValue(
    delivery?.partnerId,
    delivery?.partner_id,
    delivery?.partnerUuid,
    delivery?.partner_uuid,
    partner?.id,
    partner?.partnerId,
    partner?.partner_id,
    driverCode
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-black text-black">Order Full Details</h3>
            <p className="mt-1 text-[12px] font-semibold text-slate-400">{orderNo}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-[#fff4f6] hover:text-[#df6374]">
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[calc(90vh-74px)] overflow-y-auto bg-[#fffafa] p-6">
          {loading ? (
            <div className="flex min-h-[260px] items-center justify-center text-sm font-bold text-slate-400">Loading full order details...</div>
          ) : error ? (
            <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{error}</div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-4">
                <DetailValue label="Order number" value={orderNo} />
                <div className="rounded-[14px] border border-[#f3c7ce] bg-white px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#df6374]">Order status</p>
                  <div className="mt-2"><DetailPill tone={statusTone(order?.status || order?.orderStatus)}>{titleCase(order?.status || order?.orderStatus)}</DetailPill></div>
                </div>
                <div className="rounded-[14px] border border-[#f3c7ce] bg-white px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#df6374]">Payment status</p>
                  <div className="mt-2"><DetailPill tone={statusTone(order?.paymentStatus || order?.payment_status)}>{titleCase(order?.paymentStatus || order?.payment_status)}</DetailPill></div>
                </div>
                <DetailValue label="Grand total" value={`₹ ${money(pricing.grandTotal)}`} />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Customer & Address</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <DetailValue label="Name" value={firstValue(address?.name, address?.fullName, address?.full_name, order?.customerName, order?.customer_name)} />
                    <DetailValue label="Phone" value={firstValue(address?.phone, address?.mobile, order?.customerPhone, order?.customer_phone)} />
                    <DetailValue label="City" value={firstValue(address?.city, address?.district)} />
                    <DetailValue label="Pincode" value={firstValue(address?.pincode, address?.pinCode, address?.pin_code)} />
                    <DetailValue label="State" value={address?.state} />
                    <DetailValue label="Address" value={addressText} />
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Payment & Warehouse</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <DetailValue label="Payment method" value={titleCase(order?.paymentMethod || order?.payment_method)} />
                    <DetailValue label="Provider" value={order?.paymentProvider || order?.payment_provider || "-"} />
                    <DetailValue label="Gateway status" value={titleCase(order?.gatewayPaymentStatus || order?.gateway_payment_status)} />
                    <DetailValue label="Warehouse" value={firstValue(warehouse?.name, order?.warehouseName, warehouse?.code, order?.warehouseId, order?.warehouse_id)} />
                    <DetailValue label="Razorpay order" value={order?.razorpayOrderId || order?.razorpay_order_id || "-"} className="md:col-span-2" />
                    <DetailValue label="Razorpay payment" value={order?.razorpayPaymentId || order?.razorpay_payment_id || "-"} className="md:col-span-2" />
                  </div>
                </div>
              </div>

              <RazorpayWebhookReportCard detail={detail} order={order} />

              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Pricing Breakdown</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <DetailValue label="Subtotal" value={`₹ ${money(pricing.subtotal)}`} />
                    <DetailValue label="Delivery fee" value={`₹ ${money(pricing.deliveryFee)}`} />
                    <DetailValue label="Handling charge" value={`₹ ${money(pricing.handlingCharge)}`} />
                    <DetailValue label="Rain charge" value={`₹ ${money(pricing.rainCharge)}`} />
                    <DetailValue label="Tax" value={`₹ ${money(pricing.taxAmount)}`} />
                    <DetailValue label="Discount" value={`₹ ${money(pricing.discountAmount)}`} />
                    <DetailValue label="Grand total" value={`₹ ${money(pricing.grandTotal)}`} className="md:col-span-2" />
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Delivery Flow</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <DetailValue label="Task status" value={titleCase(delivery?.taskStatus || delivery?.task_status || "-")} />
                    <DetailValue label="Assignment" value={titleCase(delivery?.assignmentStatus || delivery?.assignment_status || "-")} />
                    <div className="rounded-[14px] border border-[#f3c7ce] bg-white px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#df6374]">Driver/Partner</p>
                      {driverRef && onOpenDriver ? (
                        <button
                          type="button"
                          onClick={() => onOpenDriver(driverRef)}
                          className="mt-1 text-left text-[13px] font-black text-[#df6374] underline-offset-2 hover:underline"
                        >
                          {driverDisplay}
                        </button>
                      ) : (
                        <p className="mt-1 break-words text-[13px] font-bold text-slate-700">{driverDisplay}</p>
                      )}
                    </div>
                    <DetailValue label="Driver phone" value={driverPhone || "-"} />
                    <DetailValue label="Vehicle" value={vehicleText} />
                    <DetailValue label="Mode" value={titleCase(delivery?.assignmentMode || delivery?.assignment_mode || "-")} />
                    <DetailValue label="Picked up at" value={formatDateTime(delivery?.pickedUpAt || delivery?.picked_up_at)} />
                    <DetailValue label="Delivered at" value={formatDateTime(delivery?.deliveredAt || delivery?.delivered_at)} />
                    <DetailValue label="Note" value={delivery?.lastStatusNote || delivery?.last_status_note || "-"} className="md:col-span-2" />
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-[16px] border border-[#f3c7ce] bg-white">
                <div className="border-b border-[#f3c7ce] bg-[#fff7f8] px-4 py-3 text-[15px] font-black text-black">Items</div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[920px] text-left text-[13px]">
                    <thead>
                      <tr className="bg-[#f5d5da] text-[#df6374]">
                        <th className="px-4 py-3 font-black">Product</th>
                        <th className="px-4 py-3 font-black">SKU</th>
                        <th className="px-4 py-3 font-black">Variant / Pack</th>
                        <th className="px-4 py-3 font-black">Qty</th>
                        <th className="px-4 py-3 font-black">Unit price</th>
                        <th className="px-4 py-3 font-black">Line total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.length ? items.map((item, index) => (
                        <tr key={item?.id || index}>
                          <td className="px-4 py-3 font-bold text-slate-700">{getProductName(item)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-500">{getProductSku(item)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-500">{getVariantLabel(item)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-500">{num(item?.qty || item?.quantity)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-500">₹ {money(item?.unitPrice || item?.unit_price)}</td>
                          <td className="px-4 py-3 font-bold text-slate-700">₹ {money(item?.lineTotal || item?.line_total)}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan="6" className="py-8 text-center font-bold text-slate-400">No items found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="relative block min-w-0">
      <span className="absolute -top-2 left-5 z-10 bg-white px-2 text-[10px] font-bold text-[#df6374]">
        {label}
      </span>
      {children}
    </label>
  );
}

function SelectField({ label, icon: Icon, value, onChange, children }) {
  return (
    <Field label={label}>
      <div className="relative">
        {Icon ? <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#df6374]" size={16} /> : null}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-[44px] w-full rounded-[14px] border border-[#f3c7ce] bg-white pl-11 pr-4 text-[13px] font-semibold text-slate-500 outline-none transition focus:border-[#df6374]"
        >
          {children}
        </select>
      </div>
    </Field>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="relative">
        <CalendarDays className="absolute left-4 top-1/2 -translate-y-1/2 text-[#df6374]" size={16} />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-[44px] w-full rounded-[14px] border border-[#f3c7ce] bg-white pl-11 pr-4 text-[13px] font-semibold text-slate-500 outline-none transition focus:border-[#df6374]"
        />
      </div>
    </Field>
  );
}

function SectionCard({ children, className = "" }) {
  return <div className={`rounded-[16px] border border-[#f3c7ce] bg-white ${className}`}>{children}</div>;
}

function SectionTitle({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-[17px] font-black text-black">{title}</h3>
        {subtitle ? <p className="mt-1 text-[12px] font-medium text-slate-400">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

function compactAxis(value) {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    notation: Math.abs(parsed) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(parsed) >= 1000 ? 1 : 0,
  }).format(parsed);
}

function chartTickIndexes(count, maxTicks = 7) {
  if (count <= 0) return [];
  if (count <= maxTicks) return Array.from({ length: count }, (_, index) => index);
  const indexes = new Set([0, count - 1]);
  for (let index = 1; index < maxTicks - 1; index += 1) {
    indexes.add(Math.round((index * (count - 1)) / (maxTicks - 1)));
  }
  return [...indexes].sort((a, b) => a - b);
}

function responsiveValueSize(value, base = 20) {
  const length = String(value ?? "0").length;
  if (length >= 17) return Math.max(base - 6, 14);
  if (length >= 14) return Math.max(base - 5, 15);
  if (length >= 11) return Math.max(base - 3, 16);
  return base;
}

function StatCard({ icon: Icon, label, value, helper }) {
  return (
    <div className="group relative min-w-0 overflow-hidden rounded-[16px] border border-[#f3c7ce] bg-gradient-to-br from-white via-white to-[#fff7f8] p-4 shadow-[0_7px_24px_rgba(223,99,116,0.06)] transition duration-200 hover:-translate-y-0.5 hover:border-[#edb2ba] hover:shadow-[0_10px_30px_rgba(223,99,116,0.11)]">
      <div className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-[#df6374] transition-transform duration-200 group-hover:scale-x-100" />
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-[#f5d5da] text-[#df6374]">
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] font-bold uppercase tracking-[0.04em] text-slate-400" title={label}>{label}</p>
          <p
            className="mt-1 whitespace-nowrap font-black leading-none tracking-[-0.035em] text-black tabular-nums"
            style={{ fontSize: `${responsiveValueSize(value, 19)}px` }}
            title={String(value ?? 0)}
          >
            {value}
          </p>
          <p className="mt-2 truncate text-[10px] font-semibold text-slate-400" title={helper}>
            <span className="text-[#48b83e]">{helper?.split(" ")?.[0] || "1+"}</span>{" "}
            {helper?.split(" ")?.slice(1)?.join(" ") || "More users"}
          </p>
        </div>
      </div>
    </div>
  );
}

function QuickBox({ icon: Icon, label, value }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-[14px] border border-[#f3c7ce] bg-gradient-to-br from-white to-[#fff8f9] p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#fff0f3] text-[#df6374]">
        <Icon size={23} />
      </div>
      <div className="min-w-0 flex-1 border-l border-dashed border-[#f3c7ce] pl-4">
        <p className="truncate text-[12px] font-semibold text-slate-400" title={label}>{label}</p>
        <p
          className="mt-1.5 whitespace-nowrap font-black leading-none tracking-[-0.035em] text-black tabular-nums"
          style={{ fontSize: `${responsiveValueSize(value, 22)}px` }}
          title={String(value ?? 0)}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function AreaChart({ items, loading }) {
  const width = 1000;
  const height = 310;
  const pad = { left: 64, right: 24, top: 24, bottom: 56 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const values = items.map((item) => Number(item?.value || 0)).filter(Number.isFinite);
  const rawMin = Math.min(...values, 0);
  const rawMax = Math.max(...values, 0);
  const spread = Math.max(rawMax - rawMin, 1);
  const extra = spread * 0.1;
  const min = rawMin < 0 ? rawMin - extra : 0;
  const max = rawMax > 0 ? rawMax + extra : 1;
  const denom = Math.max(max - min, 1);

  const points = items.map((item, index) => {
    const value = Number(item?.value || 0);
    const x = pad.left + (items.length <= 1 ? chartW / 2 : (index * chartW) / (items.length - 1));
    const y = pad.top + chartH - ((value - min) / denom) * chartH;
    return { ...item, value, x, y };
  });

  const line = points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");
  const zeroY = pad.top + chartH - ((0 - min) / denom) * chartH;
  const areaBase = Math.min(pad.top + chartH, Math.max(pad.top, zeroY));
  const area = points.length
    ? `${line} L${points[points.length - 1].x.toFixed(2)},${areaBase.toFixed(2)} L${points[0].x.toFixed(2)},${areaBase.toFixed(2)} Z`
    : "";
  const grid = Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    return {
      y: pad.top + chartH * ratio,
      value: max - (max - min) * ratio,
    };
  });
  const ticks = chartTickIndexes(points.length);
  const peakIndex = values.length ? values.indexOf(Math.max(...values)) : -1;
  const showEveryMarker = points.length <= 32;
  const enableEveryPointHover = points.length <= 180;

  if (loading) {
    return <div className="flex h-[310px] items-center justify-center text-sm font-semibold text-slate-400">Loading sales trend...</div>;
  }

  if (!items.length) {
    return <div className="flex h-[310px] items-center justify-center text-sm font-semibold text-slate-400">No sales trend found.</div>;
  }

  return (
    <div className="w-full overflow-x-auto overflow-y-hidden rounded-[14px] bg-gradient-to-b from-[#fffafb] to-white px-1 pt-1">
      <svg viewBox={`0 0 ${width} ${height}`} className="block h-auto min-w-[660px] w-full" role="img" aria-label="Sales trend chart">
        <defs>
          <linearGradient id="orderAreaPink" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={PINK} stopOpacity="0.28" />
            <stop offset="55%" stopColor={PINK} stopOpacity="0.11" />
            <stop offset="100%" stopColor={PINK} stopOpacity="0.01" />
          </linearGradient>
          <filter id="orderLineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor={PINK} floodOpacity="0.18" />
          </filter>
        </defs>

        {grid.map((gridLine, index) => (
          <g key={index}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={gridLine.y}
              y2={gridLine.y}
              stroke="#f1e6e8"
              strokeWidth="1"
              strokeDasharray={index === grid.length - 1 ? "0" : "4 5"}
            />
            <text x={pad.left - 12} y={gridLine.y + 4} textAnchor="end" fill="#94a3b8" fontSize="10" fontWeight="700">
              {compactAxis(gridLine.value)}
            </text>
          </g>
        ))}

        {min < 0 && max > 0 ? (
          <line x1={pad.left} x2={width - pad.right} y1={zeroY} y2={zeroY} stroke="#cbd5e1" strokeWidth="1.2" />
        ) : null}

        <path d={area} fill="url(#orderAreaPink)" />
        <path d={line} fill="none" stroke={PINK} strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" filter="url(#orderLineGlow)" />

        {points.map((point, index) => {
          const visible = showEveryMarker || ticks.includes(index) || index === peakIndex;
          if (!enableEveryPointHover && !visible) return null;
          return (
            <g key={`${point.key || point.label || index}-${index}`}>
              <circle cx={point.x} cy={point.y} r="9" fill="transparent">
                <title>{`${point.label || "Point"}: ${money(point.value)}`}</title>
              </circle>
              {visible ? (
                <circle cx={point.x} cy={point.y} r={index === peakIndex ? 4.8 : 3.5} fill={PINK} stroke="#fff" strokeWidth="2">
                  <title>{`${point.label || "Point"}: ${money(point.value)}`}</title>
                </circle>
              ) : null}
            </g>
          );
        })}

        {ticks.map((index) => {
          const point = points[index];
          const text = String(point?.label ?? "-");
          return (
            <g key={`x-tick-${index}`}>
              <line x1={point.x} x2={point.x} y1={height - pad.bottom + 3} y2={height - pad.bottom + 8} stroke="#d8dee8" strokeWidth="1" />
              <text
                x={point.x}
                y={height - 18}
                textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
                fill="#94a3b8"
                fontSize="10"
                fontWeight="700"
              >
                {text.length > 14 ? `${text.slice(0, 13)}…` : text}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ProductTable({ title, items, loading }) {
  const [expanded, setExpanded] = useState(false);
  const safeItems = Array.isArray(items) ? items : [];
  const shouldCollapse = safeItems.length > 5;
  const visibleItems = shouldCollapse && !expanded ? safeItems.slice(0, 5) : safeItems;

  return (
    <SectionCard className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-[17px] font-black text-black">{title}</h3>
          {shouldCollapse ? (
            <p className="mt-1 text-[11px] font-bold text-slate-400">
              Showing {visibleItems.length} of {safeItems.length} products
            </p>
          ) : null}
        </div>
        {shouldCollapse ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-2 rounded-[10px] border border-[#f3c7ce] bg-white px-3 py-2 text-[12px] font-black text-[#df6374] hover:bg-[#fff4f6]"
          >
            {expanded ? "Hide products" : "Show all products"}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-[13px]">
          <thead>
            <tr className="rounded-[10px] bg-[#f5d5da] text-[#df6374]">
              <th className="rounded-l-[8px] px-6 py-3 font-black">Product</th>
              <th className="px-6 py-3 font-black">Order</th>
              <th className="rounded-r-[8px] px-6 py-3 font-black">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="3" className="py-8 text-center font-semibold text-slate-400">Loading...</td></tr>
            ) : visibleItems.length ? (
              visibleItems.map((item) => (
                <tr key={`${title}-${item.id}`} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-6 py-3 font-medium text-slate-500">{item.name}</td>
                  <td className="px-6 py-3 font-medium text-slate-500">{item.orders}</td>
                  <td className="px-6 py-3 font-semibold text-[#62bd54]">₹{money(item.revenue)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="3" className="py-8 text-center font-semibold text-slate-400">No products found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function OrdersTable({
  items,
  loading,
  onOpenDetail,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
}) {
  const safeTotal = Math.max(0, num(total));
  const safePage = Math.max(1, num(page) || 1);
  const safeLimit = Math.max(1, num(limit) || 10);
  const safeTotalPages = Math.max(1, num(totalPages) || 1);
  const start = safeTotal ? (safePage - 1) * safeLimit + 1 : 0;
  const end = safeTotal ? Math.min(safePage * safeLimit, safeTotal) : 0;

  return (
    <SectionCard className="p-5">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-[13px]">
          <thead>
            <tr className="bg-[#f5d5da] text-[#df6374]">
              <th className="rounded-l-[8px] px-6 py-3 font-black">Order</th>
              <th className="px-6 py-3 font-black">Warehouse</th>
              <th className="px-6 py-3 font-black">Status</th>
              <th className="px-6 py-3 font-black">Items</th>
              <th className="px-6 py-3 font-black">Total</th>
              <th className="px-6 py-3 font-black">Created</th>
              <th className="rounded-r-[8px] px-6 py-3 font-black">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="py-10 text-center font-semibold text-slate-400">Loading orders...</td></tr>
            ) : items.length ? (
              items.map((item) => (
                <tr key={item.id} onClick={() => onOpenDetail?.(item)} className="cursor-pointer border-b border-slate-100 transition hover:bg-[#fff8f9] last:border-b-0">
                  <td className="px-6 py-3">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpenDetail?.(item); }} className="font-black text-[#df6374] hover:underline">{item.orderNo}</button>
                  </td>
                  <td className="px-6 py-3 font-medium text-slate-500">{item.warehouse}</td>
                  <td className="px-6 py-3 font-bold text-[#62bd54]">{titleCase(item.status)}</td>
                  <td className="px-6 py-3 font-medium text-slate-500">{item.items} items · {item.qty} qty</td>
                  <td className="px-6 py-3 font-medium text-slate-500">₹ {money(item.total)}</td>
                  <td className="px-6 py-3 font-medium text-slate-500">{formatDateTime(item.createdAt)}</td>
                  <td className="px-6 py-3">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenDetail?.(item); }}
                      className="inline-flex items-center gap-2 rounded-[10px] border border-[#f3c7ce] bg-white px-3 py-2 text-[12px] font-black text-[#df6374] hover:bg-[#fff4f6]"
                    >
                      <Eye size={14} />
                      Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="py-10 text-center font-semibold text-slate-400">No orders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-[12px] font-medium text-slate-500">
          Showing <span className="font-bold text-slate-700">{start}-{end}</span> of{" "}
          <span className="font-bold text-slate-700">{safeTotal}</span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
            Rows
            <select
              value={safeLimit}
              onChange={(e) => onLimitChange?.(Number(e.target.value))}
              disabled={loading}
              className="h-9 rounded-[10px] border border-[#f3c7ce] bg-white px-3 text-[12px] font-bold text-slate-700 outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => onPageChange?.(Math.max(1, safePage - 1))}
            disabled={safePage <= 1 || loading}
            className="h-9 rounded-[10px] border border-[#f3c7ce] bg-white px-4 text-[12px] font-bold text-slate-600 transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Prev
          </button>

          <span className="min-w-[78px] text-center text-[12px] font-bold text-slate-600">
            Page {safePage} / {safeTotalPages}
          </span>

          <button
            type="button"
            onClick={() => onPageChange?.(Math.min(safeTotalPages, safePage + 1))}
            disabled={safePage >= safeTotalPages || loading}
            className="h-9 rounded-[10px] border border-[#f3c7ce] bg-white px-4 text-[12px] font-bold text-slate-600 transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

export default function OrderReports() {
  const [warehouses, setWarehouses] = useState([]);
  const [summaryFilters, setSummaryFilters] = useState({
    warehouseId: "",
    status: "",
    paymentStatus: "",
    fromDate: "",
    toDate: "",
  });
  const [trendFilters, setTrendFilters] = useState({
    warehouseId: "",
    fromDate: "",
    toDate: "",
    period: "day",
  });
  const [productFilters, setProductFilters] = useState({
    warehouseId: "",
    fromDate: "",
    toDate: "",
  });

  const [summary, setSummary] = useState({});
  const [trend, setTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [leastProducts, setLeastProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderPage, setOrderPage] = useState(1);
  const [orderLimit, setOrderLimit] = useState(10);
  const [orderMeta, setOrderMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [trendFallbackOrders, setTrendFallbackOrders] = useState([]);
  const [loading, setLoading] = useState({ summary: true, trend: true, products: true, orders: true });
  const [error, setError] = useState("");
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState("");
  const [driverDetailOpen, setDriverDetailOpen] = useState(false);
  const [driverDetail, setDriverDetail] = useState(null);
  const [driverDetailLoading, setDriverDetailLoading] = useState(false);
  const [driverDetailError, setDriverDetailError] = useState("");

  useEffect(() => {
    let alive = true;
    warehouseService
      .listWarehouses({ page: 1, limit: 200, status: "active" })
      .then((res) => {
        if (!alive) return;
        setWarehouses(pickList(res).map(normalizeWarehouse).filter((x) => x.id));
      })
      .catch(() => {
        if (alive) setWarehouses([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadSummary = useCallback(async () => {
    setLoading((p) => ({ ...p, summary: true }));
    setError("");
    try {
      const res = await orderAnalyticsService.getSummary(summaryFilters);
      setSummary(pickObject(res));
    } catch (err) {
      setError(err?.message || "Summary API failed");
      setSummary({});
    } finally {
      setLoading((p) => ({ ...p, summary: false }));
    }
  }, [summaryFilters]);

  const loadTrend = useCallback(async () => {
    setLoading((p) => ({ ...p, trend: true }));
    try {
      const res = await orderAnalyticsService.getSalesTrend({
        warehouseId: trendFilters.warehouseId,
        fromDate: trendFilters.fromDate,
        toDate: trendFilters.toDate,
        period: trendFilters.period,
      });
      setTrend(pickList(res).map((x, i) => normalizeTrend(x, i, trendFilters.period)));
    } catch (err) {
      setError(err?.message || "Sales trend API failed");
      setTrend([]);
    } finally {
      setLoading((p) => ({ ...p, trend: false }));
    }
  }, [trendFilters]);

  const loadProducts = useCallback(async () => {
    setLoading((p) => ({ ...p, products: true }));
    try {
      const params = {
        ...productFilters,
        limit: 5,
      };
      const [top, least] = await Promise.all([
        orderAnalyticsService.getProducts({ ...params, type: "top" }),
        orderAnalyticsService.getProducts({ ...params, type: "least" }),
      ]);
      setTopProducts(pickList(top).map(normalizeProduct));
      setLeastProducts(pickList(least).map(normalizeProduct));
    } catch (err) {
      setError(err?.message || "Products API failed");
      setTopProducts([]);
      setLeastProducts([]);
    } finally {
      setLoading((p) => ({ ...p, products: false }));
    }
  }, [productFilters]);

  const loadOrders = useCallback(async () => {
    setLoading((p) => ({ ...p, orders: true }));
    try {
      const res = await orderAnalyticsService.listOrders({
        ...summaryFilters,
        page: orderPage,
        limit: orderLimit,
      });
      const normalizedOrders = pickList(res).map(normalizeOrder);
      const meta = pickMeta(res);
      const total = Math.max(0, num(meta?.total ?? meta?.totalItems ?? meta?.count ?? normalizedOrders.length));
      const resolvedLimit = Math.max(1, num(meta?.limit ?? meta?.pageSize ?? orderLimit) || orderLimit);
      const resolvedPage = Math.max(1, num(meta?.page ?? meta?.currentPage ?? orderPage) || orderPage);
      const resolvedTotalPages = Math.max(
        1,
        num(meta?.totalPages ?? meta?.pages) || Math.ceil(total / resolvedLimit) || 1
      );

      setOrders(normalizedOrders);
      setOrderMeta({
        page: resolvedPage,
        limit: resolvedLimit,
        total,
        totalPages: resolvedTotalPages,
      });

      if (orderPage === 1) setTrendFallbackOrders(normalizedOrders);

      if (orderPage > resolvedTotalPages) {
        setOrderPage(resolvedTotalPages);
      }
    } catch (err) {
      setError(err?.message || "Orders API failed");
      setOrders([]);
      setOrderMeta({ page: orderPage, limit: orderLimit, total: 0, totalPages: 1 });
      if (orderPage === 1) setTrendFallbackOrders([]);
    } finally {
      setLoading((p) => ({ ...p, orders: false }));
    }
  }, [summaryFilters, orderPage, orderLimit]);


  const openOrderDetail = useCallback(async (order) => {
    const orderId = order?.id || order?.orderId || order?.order_id;
    if (!orderId) return;

    setOrderDetailOpen(true);
    setOrderDetail(null);
    setOrderDetailError("");
    setOrderDetailLoading(true);

    try {
      const res = await ReportsService.orderDetail(orderId);
      setOrderDetail(getDetailPayload(res));
    } catch (err) {
      setOrderDetailError(err?.message || "Order detail API failed");
    } finally {
      setOrderDetailLoading(false);
    }
  }, []);


  const openDriverDetail = useCallback(async (partnerId) => {
    if (!partnerId) return;

    setDriverDetailOpen(true);
    setDriverDetail(null);
    setDriverDetailError("");
    setDriverDetailLoading(true);

    try {
      const res = await ReportsService.driverDetail(partnerId);
      setDriverDetail(getDetailPayload(res));
    } catch (err) {
      setDriverDetailError(err?.message || "Driver detail API failed");
    } finally {
      setDriverDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); loadOrders(); }, [loadSummary, loadOrders]);
  useEffect(() => { loadTrend(); }, [loadTrend]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const statValues = useMemo(() => ({
    totalOrders: num(summary?.totalOrders ?? summary?.total_orders ?? summary?.orders),
    activeOrders: num(summary?.activeOrders ?? summary?.active_orders),
    deliveredOrders: num(summary?.deliveredOrders ?? summary?.delivered_orders),
    totalSales: num(summary?.totalSales ?? summary?.total_sales ?? summary?.totalRevenue ?? summary?.total_revenue ?? summary?.revenue),
    cancelledOrders: num(summary?.cancelledOrders ?? summary?.cancelled_orders),
    deliveryFee: num(summary?.deliveryFee ?? summary?.delivery_fee ?? summary?.totalDeliveryFee ?? summary?.total_delivery_fee),
  }), [summary]);

  const fallbackTrend = useMemo(
    () => buildTrendFromOrders(trendFallbackOrders, trendFilters.period),
    [trendFallbackOrders, trendFilters.period]
  );

  const chartTrend = trend.length ? trend : fallbackTrend;

  const setSummaryFilter = useCallback((key, value) => {
    setSummaryFilters((prev) => ({ ...prev, [key]: value }));
    setOrderPage(1);
  }, []);

  const handleOrderLimitChange = useCallback((nextLimit) => {
    const normalized = [10, 20, 50, 100].includes(Number(nextLimit)) ? Number(nextLimit) : 10;
    setOrderLimit(normalized);
    setOrderPage(1);
  }, []);

  const warehouseOptions = (
    <>
      <option value="">All warehouse</option>
      {warehouses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
    </>
  );

  return (
    <div className="space-y-5 bg-[#fffafa] text-slate-700">
      {error ? (
        <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
          {error}
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[1fr_290px]">
        <div className="space-y-4">
          <SectionCard className="p-5">
            <SectionTitle title="New order time line" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SelectField label="All warehouse" icon={Store} value={summaryFilters.warehouseId} onChange={(v) => setSummaryFilter("warehouseId", v)}>{warehouseOptions}</SelectField>
              <SelectField label="All status" icon={ClipboardList} value={summaryFilters.status} onChange={(v) => setSummaryFilter("status", v)}>{STATUS_OPTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</SelectField>
              <SelectField label="All payments" icon={ReceiptText} value={summaryFilters.paymentStatus} onChange={(v) => setSummaryFilter("paymentStatus", v)}>{PAYMENT_OPTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</SelectField>
              <DateField label="Start date" value={summaryFilters.fromDate} onChange={(v) => setSummaryFilter("fromDate", v)} />
              <DateField label="End date" value={summaryFilters.toDate} onChange={(v) => setSummaryFilter("toDate", v)} />
            </div>
          </SectionCard>

          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <StatCard icon={Users} label="Total orders" value={loading.summary ? "..." : statValues.totalOrders} helper="10+ More users" />
            <StatCard icon={Users} label="Active orders" value={loading.summary ? "..." : statValues.activeOrders} helper="5+ vs Last month" />
            <StatCard icon={Users} label="Delivered order" value={loading.summary ? "..." : statValues.deliveredOrders} helper="10+ More users" />
            <StatCard icon={Users} label="Total sales" value={loading.summary ? "..." : `₹ ${money(statValues.totalSales)}`} helper="1+ More users" />
          </div>

          <SectionCard className="p-5">
            <SectionTitle title="Sales Trend Filters" subtitle="These filters are only for Sales Trend API" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <SelectField label="All warehouse" icon={Store} value={trendFilters.warehouseId} onChange={(v) => setTrendFilters((p) => ({ ...p, warehouseId: v }))}>{warehouseOptions}</SelectField>
              <DateField label="Start date" value={trendFilters.fromDate} onChange={(v) => setTrendFilters((p) => ({ ...p, fromDate: v }))} />
              <DateField label="End date" value={trendFilters.toDate} onChange={(v) => setTrendFilters((p) => ({ ...p, toDate: v }))} />
              <SelectField label="End date" icon={CalendarDays} value={trendFilters.period} onChange={(v) => setTrendFilters((p) => ({ ...p, period: v }))}>{PERIOD_OPTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</SelectField>
            </div>
          </SectionCard>
        </div>

        <SectionCard className="p-6">
          <div className="mb-6 flex items-start justify-between gap-4 border-b border-[#f3c7ce] pb-6">
            <div>
              <h3 className="text-[18px] font-black text-black">Quick Summary</h3>
              <p className="mt-1 text-[12px] font-medium text-slate-400">Extra totals from summary API</p>
            </div>
            <div className="flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#fff0f3] text-[#df6374]">
              <IndianRupee size={30} />
            </div>
          </div>
          <div className="space-y-4">
            <QuickBox icon={ReceiptText} label="Cancelled orders" value={loading.summary ? "..." : statValues.cancelledOrders} />
            <QuickBox icon={Truck} label="Delivery fee" value={loading.summary ? "..." : `₹ ${money(statValues.deliveryFee)}`} />
          </div>
        </SectionCard>
      </div>

      <SectionCard className="p-6">
        <SectionTitle
          title="Sales Trend"
          subtitle="These filters are only for Sales Trend API"
          action={
            <select
              value={trendFilters.period}
              onChange={(e) => setTrendFilters((p) => ({ ...p, period: e.target.value }))}
              className="h-[28px] rounded border border-[#df6374] bg-white px-3 text-[12px] font-bold text-[#df6374] outline-none"
            >
              {PERIOD_OPTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select>
          }
        />
        <AreaChart items={chartTrend} loading={loading.trend && !fallbackTrend.length} />
      </SectionCard>

      <SectionCard className="p-5">
        <SectionTitle title="Product Filters" subtitle="These filters are only for Products API" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SelectField label="All warehouse" icon={Store} value={productFilters.warehouseId} onChange={(v) => setProductFilters((p) => ({ ...p, warehouseId: v }))}>{warehouseOptions}</SelectField>
          <DateField label="Start date" value={productFilters.fromDate} onChange={(v) => setProductFilters((p) => ({ ...p, fromDate: v }))} />
          <DateField label="End date" value={productFilters.toDate} onChange={(v) => setProductFilters((p) => ({ ...p, toDate: v }))} />
        </div>
      </SectionCard>

      <div className="grid gap-5 xl:grid-cols-2">
        <ProductTable title="Most Sold Products" items={topProducts} loading={loading.products} />
        <ProductTable title="Least Sold Products" items={leastProducts} loading={loading.products} />
      </div>

      <OrdersTable
        items={orders}
        loading={loading.orders}
        onOpenDetail={openOrderDetail}
        page={orderMeta.page || orderPage}
        limit={orderMeta.limit || orderLimit}
        total={orderMeta.total}
        totalPages={orderMeta.totalPages}
        onPageChange={setOrderPage}
        onLimitChange={handleOrderLimitChange}
      />

      <OrderReportDetailModal
        open={orderDetailOpen}
        loading={orderDetailLoading}
        error={orderDetailError}
        detail={orderDetail}
        onOpenDriver={openDriverDetail}
        onClose={() => {
          setOrderDetailOpen(false);
          setOrderDetail(null);
          setOrderDetailError("");
        }}
      />

      <DriverDetailModal
        open={driverDetailOpen}
        loading={driverDetailLoading}
        error={driverDetailError}
        detail={driverDetail}
        onClose={() => {
          setDriverDetailOpen(false);
          setDriverDetail(null);
          setDriverDetailError("");
        }}
      />
    </div>
  );
}
